import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Application } from '../enums/application.enums';
import { CommonService } from '../modules/shared/services/common.service';
import jwt_decode from 'jwt-decode';
import { Meta, MetaDefinition } from '@angular/platform-browser';
import { MobileBridgeService } from '../services/mobile-bridge.service';
import { MOBILE_BRIDGE_EVENT_SEND } from '../constants/mobile-bridge.constant';
import { IndexedDbService } from '@services/indexed-db.service';
import { DarkPresetColorCodes } from '@modules/edit-preference/components/whitelabel-site/whitelabel-site-constant';
import { setCSSVariables } from '@utils/common.utils';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  window: Window | null;
  accessToken = new BehaviorSubject<string>('');
  authSuccessUrl = '/';
  decodedToken: any;
  formattedDate: any;
  isWhiteLabelWeb = false;
  whiteLabelData = null;
  currentLoginEmail: string | null = null;
  isLogin: boolean = false;
  userContext: { fm_user: string, admin_name: string } | null = null;
  primaryUserToken = null;
  isWhiteLabelPreview = false;
  userData: any =null;
  history: string[] = [];
  constructor(
    private readonly cms: CommonService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly metaService: Meta,
    private indexedDbService: IndexedDbService,
    private readonly mobileBridgeService: MobileBridgeService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.window = this.cms.getWindow();
    } else {
      this.window = null;
    }
    this.isLogin = this.isAuthenticated();
  }

  setUserContext(user: any) {
    this.isLogin = true;
    const expToken = JSON.stringify(user.token);
    this.decodedToken = jwt_decode(expToken);
    this.userContext = { fm_user: user.token, admin_name: user.admin_name };
  }
  async setUser(user: any, isRemember?: boolean) {
    this.isLogin = true;
    const expToken = JSON.stringify(user.token);
    this.decodedToken = jwt_decode(expToken);
    if (isPlatformBrowser(this.platformId) && this.window) {
      this.window.localStorage.setItem(
        Application.FM_USER,
        JSON.stringify({ fm_user: user.token })
      );
      await this.indexedDbService.setItem(Application.FM_USER, {
        fm_user: user.token
      });

      if (isRemember) {
        this.window.localStorage.setItem(
          Application.REFRESH_TOKEN,
          JSON.stringify({ fm_over: user.refresh_token })
        );
        await this.indexedDbService.setItem(Application.REFRESH_TOKEN, {
          fm_over: user.refresh_token
        });
      }

      this.setUserProfileTheme(user?.user?.profile_theme);
    }
  }

  async setUserProfileTheme(profileTheme: string) {
    if (isPlatformBrowser(this.platformId) && this.window) {
      const setTheme = profileTheme || Application.THEME_DARK;
      this.window.localStorage.setItem('theme', setTheme);
      await this.indexedDbService.setItem('theme', setTheme);
      this.mobileBridgeService.sendThemeCodes(setTheme)
    }
  }

  setIOSThemeMeta(bgColor: string) {
    const tag: MetaDefinition = {
      name: 'theme-color',
      content: bgColor
    };
    this.metaService.removeTag('name=theme-color');
    this.metaService.addTag(tag);
  }

  removeTheme() {
    if (isPlatformBrowser(this.platformId)) {
      this.cms.updateTheme(Application.THEME_DARK);
    }
  }

  async setRefreshTokenTime() {
    if (isPlatformBrowser(this.platformId) && this.window) {
      const endTokenTime = new Date(
        this.decodedToken?.exp * Application.ONE_THOUSAND
      );
      const date = new Date();
      this.window.localStorage.setItem(
        Application.START_TIME,
        JSON.stringify({ startTime: date })
      );
      this.window.localStorage.setItem(
        Application.END_TIME,
        JSON.stringify({ endTime: endTokenTime })
      );
      await this.indexedDbService.setItem(Application.START_TIME, { startTime: date });
      await this.indexedDbService.setItem(Application.END_TIME, { endTime: endTokenTime });
    }
  }

  isTokenExpire(): boolean | null {
    if (isPlatformBrowser(this.platformId) && this.window) {
      const expTime = JSON.parse(this.window.localStorage.getItem(Application.END_TIME)) || this.indexedDbService.getValueFromIndexedDb(Application.END_TIME);
      const date: number = new Date().getTime();
      return date > expTime?.endTime;
    }
    return null;
  }

  setTokenFor2FA(token: string) {
    this.accessToken.next(token);
    if (token) {
      this.mobileBridgeService.sendMessage(MOBILE_BRIDGE_EVENT_SEND.SET_AUTH_TOKEN, { token });
    }
  }

  async changeToken(user: any) {
    this.accessToken.next(user.fm_user);
    if (user.fm_user) {
      this.mobileBridgeService.sendMessage(MOBILE_BRIDGE_EVENT_SEND.SET_AUTH_TOKEN, { token: user.fm_user });
    }
    this.setRefreshTokenTime();
    const expToken = JSON.stringify(user.fm_user);
    this.decodedToken = jwt_decode(expToken);
    if (isPlatformBrowser(this.platformId) && this.window) {
       this.isLogin = true;
      this.window.localStorage.setItem(
        Application.FM_USER,
        JSON.stringify({ fm_user: user.fm_user })
      );
      await this.indexedDbService.setItem(Application.FM_USER, { fm_user: user.fm_user });
    }
  }
  getUserIndexedDb() {
    if (this.userContext?.fm_user) {
      return this.userContext;
    }

    if (isPlatformBrowser(this.platformId)) {
      const storedUser = JSON.parse(this.window.localStorage.getItem(Application.FM_USER)) || this.indexedDbService.getValueFromIndexedDb(Application.FM_USER);
      return storedUser ? storedUser : null;
    }

    return null;
  }
  getDelegateAuthToken() {
    if (this.primaryUserToken) {
      return this.primaryUserToken;
    }
    if (isPlatformBrowser(this.platformId) && this.window) {
      return JSON.parse(this.window.localStorage.getItem(Application.FM_PRIMARY_USER)) || this.indexedDbService.getValueFromIndexedDb(Application.FM_PRIMARY_USER);
    }
    return null;
  }

  removeDetails() {
    this.isLogin = false;
    this.userContext = null;
    if (isPlatformBrowser(this.platformId) && this.window) {
      if(!this.isWhiteLabelWeb && !environment.whiteLabelConfig)
      setCSSVariables(DarkPresetColorCodes);
      this.mobileBridgeService.sendMessage(MOBILE_BRIDGE_EVENT_SEND.REMOVE_AUTH_TOKEN, {});
      const pwaClose = this.window.localStorage.getItem('pwa-close');
      const preservedValue = window.localStorage.getItem('/recruiter@');
      const savedLocale = window.localStorage.getItem('tf_locale');
      window.localStorage.clear();
      this.indexedDbService.clear()
      this.window.sessionStorage.clear();
      if (preservedValue) {
        window.localStorage.setItem('/recruiter@', preservedValue);
      }
      if (pwaClose) {
        this.window.localStorage.setItem('pwa-close', pwaClose);
      }
      if (savedLocale) {
        window.localStorage.setItem('tf_locale', savedLocale);
      }
    }
  }

   isAuthenticated(): boolean {
    return !!(this.getUserIndexedDb() && !this.isUnAuthorizedUser());
  }

  get isAuthenticatedAndConfirmed(): boolean {
    return this.isLogin && !this.isUnAuthorizedUser();
  }

  userUnconfirmedStatus(user: any) {
    if (isPlatformBrowser(this.platformId) && this.window) {
      this.window.localStorage.setItem('isConfirm', user);
    }
  }

  getUserStatus() {
    if (isPlatformBrowser(this.platformId) && this.window) {
      return this.window.localStorage.getItem('isConfirm');
    }
    return null;
  }

  isUnAuthorizedUser(): boolean {
    return this.getUserStatus() === 'false';
  }

  readonly isWhiteLabelWebsite = () => {
    return !!environment.whiteLabelConfig;
  };

  setIsContent(isContent: any) {
    if (isPlatformBrowser(this.platformId) && this.window) {
      this.window.localStorage.setItem('isContent', isContent);
    }
  }

  setIsCreator(isCreator: any) {
    if (isPlatformBrowser(this.platformId) && this.window) {
      this.window.localStorage.setItem('isCreator', isCreator);
    }
  }

  getIsCreator(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      this.window?.localStorage.getItem('isCreator') === 'true'
    );
  }

  getIsContent(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      this.window?.localStorage.getItem('isContent') === 'true'
    );
  }

  setLocationHistory(url: string) {
    if (isPlatformBrowser(this.platformId) && this.window) {
      this.window.sessionStorage.setItem(Application.NAVIGATION_HISTORY, url);
    }
  }

  getHistory() {
    if (isPlatformBrowser(this.platformId) && this.window) {
      return this.window.sessionStorage.getItem(Application.NAVIGATION_HISTORY);
    }
    return null;
  }

  setAuthHistory(url: string) {
    if (url.includes('monetize/merchandise') || url.includes('/messages') || url.includes('/newsletters-view') || url.includes('/newsletters-list')) {
      this.authSuccessUrl = url;
    } else if (
      !url.split('?')[Application.ZERO].includes(Application.loginPath) &&
      !url.split('?')[Application.ZERO].includes(Application.Content)
    ) {
      this.authSuccessUrl = url.split('?')[Application.ZERO];
    }
  }

  getAuthHistory() {
    return this.authSuccessUrl;
  }

  rememberReturnUrlBeforeAuthNavigate(url: string): void {
    this.setAuthHistory(url);
    const last = this.history[this.history.length - Application.ONE];
    if (this.history.length === Application.ZERO || last !== url) {
      this.history.push(url);
    }
  }

  getReturnUrlAfterAuthNavigation(): string | null {
    const skip = (url: string) => !url || url === '/' || url.split('?')[Application.ZERO].includes(Application.loginPath);
    for (let i = this.history.length - Application.ONE; i >= Application.ZERO; i--) {
      if (!skip(this.history[i])) return this.history[i];
    }
    const saved = this.getAuthHistory();
    return saved && !skip(saved) ? saved : null;
  }

  setLastLoginEmail(email: string, setInLs = true): void {
    if (isPlatformBrowser(this.platformId) && setInLs) {
      localStorage.setItem(Application.LAST_LOGIN_NAME, email);
    } else if (!setInLs) {
      localStorage.removeItem(Application.LAST_LOGIN_NAME);
      this.currentLoginEmail = email;
    }
  }

  getLastLoginEmail(): string {
    if (isPlatformBrowser(this.platformId)) {
      return (
        this.currentLoginEmail ||
        localStorage.getItem(Application.LAST_LOGIN_NAME) ||
        ''
      );
    }
    return '';
  }
}
